import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { useListInvitations } from 'src/features/users/api/useListInvitations';
import CreateInvitationFormModal from 'src/features/users/components/forms/CreateInvitationFormModal';
import { CellExpiration } from 'src/features/users/components/tables/CellExpiration';
import CellInvitationActions from 'src/features/users/components/tables/CellInvitationActions';
import { userMessages } from 'src/features/users/utils/messages';
import { Invitation } from 'src/features/users/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DataTable,
    DataTablePagination,
    DataTableColumnHeader,
    DataTableControls,
} from 'src/registry/components/table';
import { CellTitleSubtitle } from 'src/registry/components/table/cells/CellTitleSubtitle';
import useFilters from 'src/registry/hooks/useFilters';

const InvitationTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const columnHelper = createColumnHelper<Invitation>();
    const permissionInvitationManage = useHasPermissions([PERMISSION.MANAGE_USERS]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });

    const { data, isLoading, isError } = useListInvitations({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const columns = [
        columnHelper.accessor('email', {
            id: 'email',
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('inviter.email', {
            id: 'inviter__email',
            header: DataTableColumnHeader,
            cell: (cellProps) =>
                CellTitleSubtitle({
                    title: cellProps.row.original.inviter.name,
                    subtitle: cellProps.row.original.inviter.email,
                    ...cellProps,
                }),
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.invitedBy),
            },
        }),
        columnHelper.accessor('sp_role.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.role),
            },
        }),
        columnHelper.accessor('expires_at', {
            header: DataTableColumnHeader,
            cell: CellExpiration,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.expires),
            },
        }),
        ...(permissionInvitationManage
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellInvitationActions,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Invitation, any>
            columns={columns}
            data={data}
            pagination={pagination}
            sorting={sorting}
            isLoading={isLoading}
            isError={isError}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(userMessages.invitations),
                entryName: formatMessage(userMessages.invitation),
                entryNames: formatMessage(userMessages.invitations),
                placeholder: formatMessage(commonMessages.email),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionInvitationManage && { newEntry: CreateInvitationFormModal }),
            }}
        />
    );
};

export { InvitationTable };
